/**
 * Open Library API integration
 * Provides book metadata and covers as alternative/supplement to Google Books
 */

/**
 * Fetch book data from Open Library by ISBN
 */
export const fetchBookByISBN = async (isbn) => {
  if (!isbn) return null;

  try {
    // Clean ISBN (remove hyphens)
    const cleanIsbn = isbn.replace(/-/g, "");

    const response = await fetch(
      `https://openlibrary.org/isbn/${cleanIsbn}.json`,
      {
        signal: AbortSignal.timeout(5000), // 5 second timeout
      }
    );

    if (!response.ok) return null;

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching from Open Library:", error);
    return null;
  }
};

/**
 * Fetch detailed work data from Open Library
 */
export const fetchWorkDetails = async (workKey) => {
  if (!workKey) return null;

  try {
    const response = await fetch(`https://openlibrary.org${workKey}.json`, {
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching work details from Open Library:", error);
    return null;
  }
};

/**
 * Fetch author details from Open Library
 */
export const fetchAuthorDetails = async (authorKey) => {
  if (!authorKey) return null;

  try {
    const response = await fetch(`https://openlibrary.org${authorKey}.json`, {
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) return null;

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching author from Open Library:", error);
    return null;
  }
};

/**
 * Get comprehensive book data from Open Library
 */
export const getOpenLibraryBookData = async (isbn) => {
  try {
    // Fetch edition data
    const editionData = await fetchBookByISBN(isbn);
    if (!editionData) return null;

    // Fetch work data if available (has more complete info)
    let workData = null;
    if (editionData.works && editionData.works.length > 0) {
      workData = await fetchWorkDetails(editionData.works[0].key);
    }

    // Extract and normalize the data
    return {
      title: editionData.title || workData?.title,
      subtitle: editionData.subtitle,
      authors: editionData.authors || workData?.authors || [],
      publishDate: editionData.publish_date,
      publishers: editionData.publishers || [],
      numberOfPages: editionData.number_of_pages || workData?.number_of_pages,
      subjects: workData?.subjects || [],
      description:
        typeof workData?.description === "string"
          ? workData.description
          : workData?.description?.value || editionData.description,
      covers: editionData.covers || workData?.covers || [],
      isbn: isbn,
      openLibraryId: editionData.key,
      workKey: editionData.works?.[0]?.key,
    };
  } catch (error) {
    console.error("Error getting Open Library book data:", error);
    return null;
  }
};

/**
 * Merge book data from Google Books and Open Library
 * Prefers the most complete/accurate data from each source
 */
export const mergeBookData = (googleData, openLibraryData) => {
  if (!googleData) return null;

  // If no Open Library data, return Google data as-is
  if (!openLibraryData) {
    return {
      ...googleData,
      dataSource: "google",
    };
  }

  // Merge data, preferring the most complete source for each field
  return {
    title: googleData.title, // Google usually has better title formatting
    subtitle: openLibraryData.subtitle || googleData.subtitle,
    authors:
      googleData.authors && googleData.authors.length > 0
        ? googleData.authors
        : openLibraryData.authors,
    // Prefer Open Library page count if available (often more accurate)
    pages: openLibraryData.numberOfPages || googleData.pages,
    // Prefer the longer description
    description:
      (openLibraryData.description?.length || 0) >
      (googleData.description?.length || 0)
        ? openLibraryData.description
        : googleData.description,
    // Prefer Open Library publish date if it's more specific
    publishDate: openLibraryData.publishDate || googleData.publishDate,
    // Use Google's genre/categories (Open Library subjects are too granular)
    genre: googleData.genre,
    categories: googleData.tags,
    // Keep both subjects for potential future use
    subjects: openLibraryData.subjects,
    // Keep Google's rating data
    rating: googleData.rating,
    totalRatings: googleData.totalRatings,
    // Metadata about sources
    dataSource: "merged",
    openLibraryId: openLibraryData.openLibraryId,
    googleBooksId: googleData.googleBooksId,
  };
};
